import { Link } from 'react-router-dom';
import { ArrowRight, Target, Wrench, MessageCircle } from 'lucide-react';

export const ServicesSection = () => {
  const services = [
    {
      icon: Target,
      title: 'Strategic Consulting',
      subtitle: 'Hybrid Cloud Infrastructure Strategy & Roadmaps',
      items: [
        'Multi-cloud architecture design',
        'AI/LLM infrastructure planning',
        'Cost optimization analysis',
        'Technology vendor selection',
        '90-day transformation roadmaps',
      ],
      engagement: '4-12 weeks',
      format: 'Remote + on-site workshops',
      featured: true,
    },
    {
      icon: Wrench,
      title: 'Implementation Support',
      subtitle: 'Hands-On Architecture & Deployment',
      items: [
        'Multi-model LLM deployment',
        'Kubernetes/container orchestration',
        'DevSecOps pipeline setup',
        'SRE practices implementation',
        'Team training & knowledge transfer',
      ],
      engagement: '3-6 months',
      format: 'Embedded with your team',
      featured: false,
    },
    {
      icon: MessageCircle,
      title: 'Advisory Services',
      subtitle: 'Ongoing Technical Advisory',
      items: [
        'Monthly architecture reviews',
        'Technology landscape updates',
        'Vendor roadmap analysis',
        'Team mentorship',
        'Best practices guidance',
      ],
      engagement: '6-12 month retainers',
      format: 'Monthly sessions + async',
      featured: false,
    },
  ];

  return (
    <section className="py-20 md:py-32 bg-white" data-testid="services-section">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
            Services
          </p>
          <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-[#2C3E50] mb-4">
            How I Can Help
          </h2>
          <p className="text-[#64748B] text-lg">
            From strategic planning to hands-on implementation, I provide comprehensive consulting services tailored to your organization's needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl border border-slate-200 p-8 card-hover ${
                service.featured ? 'md:row-span-1 border-l-4 border-l-[#3498DB]' : ''
              }`}
              data-testid={`service-card-${index}`}
            >
              {/* Icon */}
              <div className="w-12 h-12 rounded-lg bg-[#3498DB]/10 flex items-center justify-center mb-6">
                <service.icon className="text-[#3498DB]" size={24} />
              </div>

              {/* Content */}
              <h3 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50] mb-2">
                {service.title}
              </h3>
              <p className="text-[#64748B] text-sm mb-4">{service.subtitle}</p>

              <ul className="space-y-2 mb-6">
                {service.items.map((item, idx) => (
                  <li key={idx} className="text-[#2C3E50] text-sm flex items-start gap-2">
                    <span className="text-[#3498DB] mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>

              {/* Meta */}
              <div className="pt-4 border-t border-slate-100 space-y-2">
                <p className="text-sm">
                  <span className="text-[#64748B]">Engagement:</span>{' '}
                  <span className="text-[#2C3E50] font-medium">{service.engagement}</span>
                </p>
                <p className="text-sm">
                  <span className="text-[#64748B]">Format:</span>{' '}
                  <span className="text-[#2C3E50] font-medium">{service.format}</span>
                </p>
              </div>

              {/* Link */}
              <Link
                to="/consulting"
                className="inline-flex items-center text-[#3498DB] font-medium text-sm mt-6 hover:gap-3 gap-2 transition-all duration-200"
              >
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
